import { inject, Injectable } from '@angular/core';
import { DocumentTypeEditPermissionEnum } from '@app/core/enums/document/document-type-edit-permission.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { UserRoleDefinition, UserRoleEnum } from '@app/core/enums/user-role-enum';
import { FieldPermission } from '@app/core/model/client/group';
import { User } from '@app/core/model/client/user';
import { UserRole } from '@app/core/model/client/user-role';
import { IRelatedAsset } from '@app/core/model/entities/asset/asset';
import { BiReport } from '@app/core/model/entities/bi-report/bi-report';
import { Document } from '@app/core/model/entities/document/document';
import { Entity } from '@app/core/model/entities/entity';
import { BiReportsService } from '@app/features/main/views/management/organization/bi-reports/bi-reports.service';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env/environment';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import LogRocket from 'logrocket';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class AccessManager {
  public userRoleMap = new Map<string, UserRoleDefinition>();
  private _currentUser: User | null;
  private _userBiReports = new BehaviorSubject<BiReport[] | undefined>(void 0);

  private readonly subscriptionGraphQlFragment = gql`
    fragment SubscriptionInfoForGrid on ClientSubscription {
      id
      startDate
      endDate
      contractId
    }
  `;

  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private appManager = inject(AppManager);
  private biReportsService = inject(BiReportsService);

  constructor() {
    Object.keys(UserRoleDefinition).forEach((userRoleEnumElement) => {
      const userRole = UserRoleDefinition[userRoleEnumElement] as UserRoleDefinition;
      this.userRoleMap.set(userRole.roleCode, userRole);
    });
  }

  /**
   * Load the current User's data from the API.
   * @return An Observable that emits the current User.
   */
  public loadCurrentUser(): Observable<User> {
    const query = gql`
      query ME {
        me {
          id
          name
          email
          isTb
          clients {
            id
            isTb
            organizationId
            subscriptions {
              ...SubscriptionInfoForGrid
            }
          }
          groups {
            assetIds
            assetTypeIds
            allAssetAccess
            allAssetTypeAccess
            entityTypes
            fields {
              code
              entityType
              permission
            }
            client {
              organizationId
            }
          }
          configurableSubscriptionModules {
            module {
              code
              isConfigurable
            }
          }
          userRole {
            id
            isTb
            code
            order
            activePermissions {
              id
              code
            }
          }
          globalPermissions {
            code
          }
          workspaceId
        }
      }
      ${this.subscriptionGraphQlFragment}
    `;

    return this.apiService.query({query})
      .pipe(
        map(data => plainToInstance(User, data['me'])),
        map(me => {
          this._currentUser = me;

          if (environment.logRocket.enabled) {
            // Add user info in LogRocket monitoring
            LogRocket.identify(me.id, {
              email: me.email
            });
          }

          return me;
        })
      );
  }

  /**
   * Currently authenticated user.
   */
  public get currentUser(): User | null {
    return this._currentUser;
  }

  /**
   * Getter method to retrieve an observable of bi reports for the current user.
   * If the reports are already loaded, it returns the existing observable, otherwise it requests the API.
   * @return An observable that emits an array of BI reports for the current user.
   */
  public get userBiReports$(): Observable<BiReport[]> {
    if (this._userBiReports.value) return this._userBiReports.asObservable();
    return this.biReportsService.loadUserBiReports(this._currentUser.id);
  }

  /**
   * Check whether a permission is granted to the current User.
   * @param permissionCode Permission's code.
   * @param checkGlobalPermissions Whether to check for global permissions. If false, only check permissions for current
   *                               organization. Defaults to false.
   * @return True if the permission is granted, false otherwise.
   */
  public hasAccess(permissionCode: string, checkGlobalPermissions: boolean = false): boolean {
    return this._currentUser?.getUserPermissions(checkGlobalPermissions)
        .some(permission => permission.code === permissionCode)
      ?? false;
  }

  /**
   * Check whether all the permissions are granted to the current user.
   * @param permissionCodes Permissions to check.
   * @return True if the current User has ALL permissions granted, false otherwise.
   */
  public hasAllNeededPermissions(permissionCodes: string[]): boolean {
    return permissionCodes.every(permissionCode => this.hasAccess(permissionCode)) ?? false;
  }

  /**
   * Check whether the current user is allowed to manage another user who has a given role.
   * @param role Role of the other user to check against the current user's role.
   * @return True if the current user's role allows them to manage a user with the given role, false otherwise.
   */
  public canManageRole(role: UserRole): boolean {
    const currentUserRole = this.userRoleMap.get(this._currentUser.userRole.code);
    const otherUserRole = this.userRoleMap.get(role.code);

    // As the UserRoleDefinition defines how each role has control
    // over which role, if a role is in a definition, then it has control over it
    return currentUserRole.controlOver.includes(otherUserRole.roleCode);
  }

  /**
   * Check whether the current user is granted access to an organization.
   * @param organizationId ID of the organization to check for access permission.
   * @return True if the current user has access to the organization, false otherwise
   */
  public hasAccessToOrganization(organizationId: string): boolean {
    return this._currentUser?.clients.some(client => client.organizationId === organizationId)
      ?? false;
  }

  /**
   * Determines whether the current user has access to a related asset based on their group permissions.
   *
   * @param relatedAsset The related asset to check access for. Contains information such as the asset ID and type ID.
   * @param organizationId The ID of the organization the asset belongs to.
   * @return Returns true if the user has access to the related asset, otherwise false.
   */
  public hasAccessToRelatedAsset(relatedAsset: IRelatedAsset, organizationId: string): boolean {
    const currentUserGroup = this.currentUser.groups?.firstOrNull(group => group.client.organizationId === organizationId);
    if (this.isSuperAdmin() || (currentUserGroup?.allAssetAccess && currentUserGroup?.allAssetTypeAccess)) return true;

    return (currentUserGroup?.allAssetAccess || currentUserGroup.assetIds.includes(relatedAsset.id)) &&
      (currentUserGroup?.allAssetTypeAccess || currentUserGroup.assetTypeIds.includes(relatedAsset.typeId));
  }

  /**
   * Determines whether the current user has read access to a specific field of a given entity type.
   * Write access implies read access.
   *
   * @param code - The code representing the specific field to check for read access.
   * @param entityType - The entity type associated with the field.
   * @return true if the user has read access to the field, otherwise false.
   */
  public hasFieldReadAccess(code: string, entityType: EntityTypeEnum): boolean {
    if (this.hasAllFieldAccess(entityType)) return true;

    const group = this._currentUser?.groups?.firstOrNull(group => group.client.organizationId === this.appManager.currentOrganization.id);
    const hasEntityAllFieldAccess = group?.entityTypes?.includes(entityType);
    if (hasEntityAllFieldAccess) return true;

    return group?.fields
        ?.firstOrNull(field => field.code === code && field.entityType === entityType)?.permission
        ?.let(permission => permission === FieldPermission.READ || permission === FieldPermission.WRITE)
      ?? false;
  }

  /**
   * Determines whether the current user has write access to a specific field of a given entity type.
   *
   * @param code - The code representing the specific field to check for write access.
   * @param entityType - The entity type associated with the field.
   * @return true if the user has write access to the field, otherwise false.
   */
  public hasFieldWriteAccess(code: string, entityType: EntityTypeEnum): boolean {
    if (this.hasAllFieldAccess(entityType)) return true;

    const group = this._currentUser?.groups?.firstOrNull(group => group.client.organizationId === this.appManager.currentOrganization.id);
    const hasEntityAllFieldAccess = group?.entityTypes?.includes(entityType);
    if (hasEntityAllFieldAccess) return true;

    return group?.fields
      ?.firstOrNull(field => field.code === code && field.entityType === entityType)
      ?.permission === FieldPermission.WRITE;
  }

  /**
   * Determines whether the current user has access to the fields of a given entity type
   * because of its role or because the given entity type does not require specifics field access.
   *
   * @param entityType - The entity type associated with the fields.
   * @return true if the user has access to the fields without needing additional permissions, otherwise false.
   */
  private hasAllFieldAccess(entityType: EntityTypeEnum): boolean {
    if (this.isSuperAdmin()) return true;

    // Check if field read access is relevant for entity type
    const modulesWithFieldAccess = this._currentUser?.configurableSubscriptionModules
      ?.filter(subscriptionModule => subscriptionModule.module.isConfigurable)
      ?.map(subscriptionModule => subscriptionModule.module.code)
      // TODO TTT-5015 Remove manual logic
      // Manually add a lease module if the occupant module exists, since we can select fields permissions on leases but no lease module exists.
      ?.also(modules => {
        const occupantModule = modules.find(module => module == EntityTypeEnum.OCCUPANT);
        if (occupantModule) modules.push(EntityTypeEnum.LEASE);
      });

    return !modulesWithFieldAccess.includes(entityType);
  }

  /**
   * Return whether the current user is a super administrator.
   */
  public isSuperAdmin(): boolean {
    return this._currentUser?.userRole.code === UserRoleEnum.SUPER_ADMIN;
  }

  /**
   * Check whether the current user has a given role.
   * @param userRoleCode Role to check against the current user.
   * @return True if the role is assigned to the current user, false otherwise.
   */
  public isUserRole(userRoleCode: UserRoleEnum): boolean {
    return this._currentUser?.userRole.code === userRoleCode;
  }

  /**
   * Depending on the type of entity, return custom edit permissions.
   * Used when an entity edition need permissions from multiple entities.
   * For example to edit a check document, edit_document and edit_check permission are needed.
   *
   * @param entity
   * @return string[] custom permissions needed for the edition of the entity
   */
  public getCustomEditPermissionByEntityType(entity: Entity): string[] | undefined {
    let extraPermissions: string[];
    if (entity instanceof Document) {
      extraPermissions = [PermissionEnum.EDIT_DOCUMENT];
      const relatedEntityEditPermission = DocumentTypeEditPermissionEnum[entity.type];
      if (relatedEntityEditPermission !== void 0) {
        extraPermissions.push(DocumentTypeEditPermissionEnum[entity.type]);
      }
    }
    return extraPermissions;
  }

  /**
   * Log out the currently authenticated user, clear currentUser as well as the AppManager's current Organization.
   * @return Observable that emits once logout and cleanup are completed.
   */
  public logout(): Observable<void> {
    return this.authService.logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    })
      .pipe(
        // Clear current user and current organization
        tap(() => {
          this._currentUser = null;
          this.appManager.currentOrganization = null;
        })
      );
  }
}
